// /guru — Portal guru (Prompt D)
// - Tab "Kelas Saya": senarai murid dengan badge status + tunggakan; klik -> rincian.
//   Guru boleh MEMBANTU memuat naik bukti bayaran bagi pihak orangtua (untuk murid
//   dalam kelasnya) — ciri penuh portal orangtua: bil + butang Bayar, status bukti,
//   sejarah resit + unduh PDF. Guru TIDAK boleh mencatat/mengesahkan bayaran.
// - Tab "Anak Saya": fungsi penuh portal orangtua (untuk anak sendiri).
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, AlertTriangle, GraduationCap, Heart, ReceiptText, Info, Loader2, PencilLine, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import { paidByInvoice, invoiceOutstanding, studentSummary, groupBy } from '../../lib/fees';
import { formatRM } from '../../lib/format';
import PageHeader from '../../components/PageHeader';
import { TableSkeleton } from '../../components/Skeletons';
import EmptyState from '../../components/EmptyState';
import StatusPill from '../../components/StatusPill';
import UserAvatar from '../../components/UserAvatar';
import DataTable from '../../components/DataTable';
import StudentBillingPanel from '../../components/StudentBillingPanel';
import StudentDataForm from '../../components/StudentDataForm';
import { Button } from '../../components/ui/button';
import { StaggerList, StaggerItem } from '../../components/Motion';
import { CenterMorphModal, CenterMorphModalContent } from '../../components/beui/CenterMorphModal';
import OrangtuaDashboard from '../orangtua/Dashboard';

export default function GuruDashboard() {
  const { t } = useTranslation();
  const { school, profile } = useAuth();
  const [tab, setTab] = useState('kelas');
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null); // { student..., bills, proofs, receipts, payments, extrasLoading }
  const [dataForm, setDataForm] = useState(null); // murid untuk form Lengkapkan Data
  const [ingatkanBusy, setIngatkanBusy] = useState(false);
  const [ingatkanMsg, setIngatkanMsg] = useState('');
  const [hasChildren, setHasChildren] = useState(false);
  const profileId = profile?.id;

  useEffect(() => {
    if (!profileId) return;
    (async () => {
      const [{ data: tcl }, { data: tclPending }, { data: links }, { data: grp }] = await Promise.all([
        supabase.from('teacher_class_links').select('class_id').eq('status', 'lulus'),
        supabase.from('teacher_class_links').select('class_id').eq('status', 'pending'),
        supabase.from('parent_student_links').select('student_id').eq('status', 'lulus').limit(1),
        supabase.from('groups').select('id, nama, class_id, hoca_id').then((r) => ({ data: r.error ? [] : r.data })),
      ]);
      setPendingCount((tclPending || []).length);
      setHasChildren((links || []).length > 0);
      const mine = (grp || []).filter((g) => g.hoca_id && g.hoca_id === profileId);
      setMyGroups(mine);
      const classIds = [...new Set([
        ...((tcl || []).map((x) => x.class_id)),
        ...(mine.map((g) => g.class_id)),
      ])];
      if (classIds.length === 0) { setLoading(false); return; }
      const [{ data: cls }, { data: students }, { data: invoices }, { data: payments }, { data: tariffs }] = await Promise.all([
        supabase.from('classes').select('id, nama').in('id', classIds),
        supabase.from('students').select('id, nama, no_pelajar, class_id, group_id').in('class_id', classIds).order('nama'),
        supabase.from('invoices').select('id, student_id, jumlah_rm, jumlah_bayar_rm, diskon_nota, status, due_date, tariff_id, periode'),
        supabase.from('payments').select('id, invoice_id, student_id, jumlah_rm, kaedah, tanggal, voided'),
        supabase.from('tariffs').select('id, nama'),
      ]);
      const paidMap = paidByInvoice((payments || []).filter((p) => !p.voided));
      const cMap = Object.fromEntries((cls || []).map((c) => [c.id, c.nama]));
      const gMap = Object.fromEntries((grp || []).map((g) => [g.id, g.nama]));
      const tMap = Object.fromEntries((tariffs || []).map((x) => [x.id, x.nama]));
      const invByStudent = groupBy(invoices || [], 'student_id');
      const built = (students || []).map((s) => {
        const invs = invByStudent[s.id] || [];
        return {
          ...s, kelas: cMap[s.class_id] || '-', grup: gMap[s.group_id] || '-',
          ...studentSummary(invs, paidMap),
          bills: invs.map((inv) => ({
            ...inv, tarifNama: tMap[inv.tariff_id] || 'Yuran',
            dibayar: paidMap[inv.id] || 0, baki: invoiceOutstanding(inv, paidMap),
          })),
        };
      });
      setClasses(cls || []);
      setRows(built);
      setLoading(false);
    })();
  }, [profileId]);

  // Buka rincian murid + muat bukti & resit untuk panel bil
  const openDetail = async (r) => {
    setDetail({ ...r, proofs: [], receipts: [], payments: [], extrasLoading: true });
    setIngatkanMsg('');
    const [{ data: payData }, { data: proofData }] = await Promise.all([
      supabase.from('payments').select('id, invoice_id, student_id, jumlah_rm, kaedah, tanggal, voided').eq('student_id', r.id),
      supabase.from('payment_proofs').select('id, invoice_id, student_id, nama_file, status, alasan_tolak, dibuka_bendahara_at, created_at').eq('student_id', r.id).order('created_at', { ascending: false }),
    ]);
    const payList = (payData || []).filter((p) => !p.voided);
    const { data: rcpts } = payList.length
      ? await supabase.from('receipts').select('id, payment_id, no_resit, drive_file_id, tanggal_cetak').in('payment_id', payList.map((p) => p.id))
      : { data: [] };
    setDetail((d) => d && d.id === r.id
      ? { ...d, proofs: proofData || [], receipts: rcpts || [], payments: payList, extrasLoading: false }
      : d);
  };

  const totalTunggakan = rows.reduce((a, r) => a + r.outstanding, 0);
  const muridTunggakan = rows.filter((r) => r.outstanding > 0).length;

  // Hantar peringatan tunggakan satu arah ke ibu bapa (RPC, anti-spam 24 jam)
  const ingatkan = async () => {
    if (!detail || ingatkanBusy) return;
    if (!window.confirm(t('ingatkanConfirm'))) return;
    setIngatkanBusy(true); setIngatkanMsg('');
    try {
      const { error } = await supabase.rpc('guru_ingatkan_tunggakan', { p_student_id: detail.id });
      if (error) throw error;
      setIngatkanMsg(t('ingatkanBerjaya'));
    } catch (e) {
      setIngatkanMsg(e.message === 'ALREADY_REMINDED' ? t('ingatkanSudah') : e.message);
    }
    setIngatkanBusy(false);
  };

  return (
    <div>
      <PageHeader
        title={t('dashboardKelas')}
        subtitle={t('guruReadOnly')}
        breadcrumbs={[{ label: t('utama') }, { label: t('dashboardKelas') }]}
      />

      {pendingCount > 0 && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 p-4" data-testid="pending-banner">
          <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800 dark:text-amber-200">{t('permohonanDiproses')}</p>
            <p className="text-sm text-amber-700 dark:text-amber-300">{t('permohonanGuruMenunggu', { n: pendingCount })}</p>
          </div>
        </div>
      )}

      {hasChildren && (
        <div className="flex gap-2 mb-5">
          {[
            { k: 'kelas', l: t('kelasSaya'), icon: GraduationCap },
            { k: 'anak', l: t('anakSayaTab'), icon: Heart },
          ].map(({ k, l, icon: Icon }) => (
            <button key={k} onClick={() => setTab(k)}
              className={`h-10 px-4 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors ${tab === k ? 'bg-brand text-white' : 'bg-card border border-border text-muted-foreground hover:bg-muted'}`}>
              <Icon className="h-4 w-4" />{l}
            </button>
          ))}
        </div>
      )}

      {tab === 'anak' ? <OrangtuaDashboard /> : (
        loading ? <TableSkeleton /> : classes.length === 0 ? (
          <EmptyState icon={GraduationCap} title={t('tiadaKelas')} description={t('tiadaKelasDesc')} />
        ) : (
          <>
            <div className="mb-5 flex items-start gap-3 rounded-2xl border border-sky-200 dark:border-sky-800/80 bg-sky-50 dark:bg-sky-950/50 p-4">
              <Info className="h-5 w-5 text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
              <p className="text-sm text-sky-800 dark:text-sky-200">{t('bantuBayarHint')}</p>
            </div>

            <StaggerList className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <StaggerItem className="rounded-2xl border border-border bg-card p-4 sm:p-5">
                <span className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-brand-light text-brand flex items-center justify-center"><Users className="h-5 w-5" /></span>
                <p className="mt-3 sm:mt-4 text-sm text-muted-foreground">{t('jumlahMurid')}</p>
                <p className="text-2xl sm:text-3xl font-black text-foreground">{rows.length}</p>
              </StaggerItem>
              <StaggerItem className="rounded-2xl border border-border bg-card p-4 sm:p-5">
                <span className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-amber2-light text-amber-600 dark:text-amber-400 flex items-center justify-center"><AlertTriangle className="h-5 w-5" /></span>
                <p className="mt-3 sm:mt-4 text-sm text-muted-foreground">{t('muridBertunggak')}</p>
                <p className="text-2xl sm:text-3xl font-black text-foreground">{muridTunggakan}</p>
              </StaggerItem>
              <StaggerItem className="rounded-2xl border border-border bg-card p-4 sm:p-5">
                <span className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 flex items-center justify-center"><AlertTriangle className="h-5 w-5" /></span>
                <p className="mt-3 sm:mt-4 text-sm text-muted-foreground">{t('jumlahTunggakanKelas')}</p>
                <p className="text-xl sm:text-3xl font-black font-mono text-red-600 dark:text-red-400 tabular">{formatRM(totalTunggakan)}</p>
              </StaggerItem>
            </StaggerList>

            <DataTable
              testid="guru"
              rows={rows}
              defaultTab={myGroups.length > 0 ? `grup-${myGroups[0].id}` : classes.length > 0 ? classes[0].id : 'semua'}
              tabs={[
                { key: 'semua', label: t('semua') },
                ...myGroups.map((g) => ({ key: `grup-${g.id}`, label: `${t('grupSaya')}: ${g.nama}`, filter: (r) => r.group_id === g.id })),
                ...classes.map((c) => ({ key: c.id, label: c.nama, filter: (r) => r.class_id === c.id })),
              ]}
              searchKeys={[(r) => r.nama, (r) => r.kelas, (r) => r.grup]}
              statusOptions={[
                { key: 'belum', label: t('stBelum'), filter: (r) => r.status === 'belum' },
                { key: 'separa', label: t('stSepara'), filter: (r) => r.status === 'separa' },
                { key: 'lunas', label: t('stLunas'), filter: (r) => r.status === 'lunas' },
              ]}
              columns={[
                { key: 'nama', header: t('murid'), sortable: true, sortValue: (r) => r.nama,
                  render: (r) => (
                    <div className="flex items-center gap-3 min-w-0">
                      <UserAvatar nama={r.nama} size={36} />
                      <span className="font-semibold text-foreground truncate">{r.nama}</span>
                    </div>
                  ) },
                { key: 'kelas', header: t('kelas'), sortable: true, sortValue: (r) => r.kelas, render: (r) => <span className="text-muted-foreground">{r.kelas}</span> },
                { key: 'grup', header: t('grup'), sortable: true, sortValue: (r) => r.grup, render: (r) => <span className="text-muted-foreground">{r.grup}</span> },
                { key: 'status', header: t('status'), render: (r) => <StatusPill status={r.status} /> },
                { key: 'tunggakan', header: t('tunggakan'), align: 'right', sortable: true, sortValue: (r) => r.outstanding,
                  render: (r) => (
                    <span className={`font-mono font-bold tabular ${r.outstanding > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>{formatRM(r.outstanding)}</span>
                  ) },
              ]}
              onRowClick={openDetail}
              rowTestId={(r) => `guru-row-${r.id}`}
              empty={{ icon: Users, title: t('tiadaMurid'), description: t('tiadaMuridDesc') }}
            />
            <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5"><ReceiptText className="h-3.5 w-3.5" />{t('klikMurid')}</p>
          </>
        )
      )}

      {/* Rincian murid — bil + bantu muat naik bukti + status bukti + resit */}
      <CenterMorphModal open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <CenterMorphModalContent ariaLabel={`${detail?.nama || ''} · ${detail?.kelas || ''}`} className="max-w-lg bg-card">
          <div className="p-6 max-h-[85vh] overflow-y-auto">
            <div className="mb-4 pr-8 flex items-start justify-between gap-3">
              <h2 className="text-lg font-extrabold text-foreground">{detail?.nama} · {detail?.kelas}</h2>
              <Button
                variant="outline"
                className="h-9 shrink-0 text-xs"
                onClick={() => setDataForm(detail)}
                data-testid="lengkapkan-data-btn"
              >
                <PencilLine className="h-3.5 w-3.5 mr-1.5" />{t('lengkapkanData')}
              </Button>
            </div>
          {detail && (
            <div className="space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <StatusPill status={detail.status} />
                <span className="text-sm text-muted-foreground">{t('tunggakan')}: <b className="font-mono text-foreground">{formatRM(detail.outstanding)}</b></span>
                {detail.outstanding > 0 && (
                  <Button
                    variant="outline"
                    className="h-8 text-xs border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/60"
                    onClick={ingatkan}
                    disabled={ingatkanBusy}
                    data-testid="ingatkan-btn"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
                    {ingatkanBusy ? t('memuatkan') : t('ingatkan')}
                  </Button>
                )}
              </div>
              {ingatkanMsg && (
                <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg px-2.5 py-1.5">{ingatkanMsg}</p>
              )}
              {detail.extrasLoading ? (
                <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" />{t('memuatkan')}
                </div>
              ) : (
                <StudentBillingPanel
                  student={detail}
                  bills={detail.bills}
                  proofs={detail.proofs}
                  receipts={detail.receipts}
                  payments={detail.payments}
                  school={school}
                  onUploaded={() => openDetail(rows.find((r) => r.id === detail.id) || detail)}
                />
              )}
            </div>
          )}
          </div>
        </CenterMorphModalContent>
      </CenterMorphModal>

      {/* Form lengkapkan data murid (No. KP + Telefon Orangtua) */}
      <StudentDataForm
        student={dataForm}
        open={!!dataForm}
        onOpenChange={(o) => !o && setDataForm(null)}
      />
    </div>
  );
}
